<template>
  <div class="w-full">
    <Head title="Aktualności" />
    <h1 class="mb-8 text-3xl font-bold">Aktualności</h1>

    <div class="mb-6 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label
          for="article-filter-title"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Tytuł
        </label>
        <input
          id="article-filter-title"
          v-model="form.title"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <div>
        <label
          for="article-filter-slug"
          class="mb-1 block text-xs font-medium text-gray-600"
        >
          Slug
        </label>
        <input
          id="article-filter-slug"
          v-model="form.slug"
          type="search"
          autocomplete="off"
          placeholder="Szukaj…"
          class="form-input w-[200px] py-1.5 text-sm"
        >
      </div>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="text-sm text-gray-500 hover:text-gray-800"
        @click="resetFilters"
      >
        Wyczyść
      </button>
      <Link class="btn-indigo ml-auto" :href="routes.new()">
        + Nowa aktualność
      </Link>
    </div>

    <div class="overflow-hidden rounded-sm bg-white shadow-sm">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="px-6 py-4">Miniatura</th>
            <th class="px-6 py-4">Tytuł</th>
            <th class="px-6 py-4">Witryna</th>
            <th class="px-6 py-4">Slug</th>
            <th class="px-6 py-4">Status</th>
            <th class="px-6 py-4">Opublikowano</th>
            <th class="w-px px-6 py-4" />
          </tr>
        </thead>
        <tbody v-if="articles.data.length">
          <tr
            v-for="article in articles.data"
            :key="article.id"
            class="hover:bg-gray-50"
          >
            <td class="border-t px-6 py-3">
              <img
                v-if="article.cover_image_url"
                :src="article.cover_image_url"
                :alt="article.title || 'Miniatura'"
                class="h-16 w-28 rounded object-cover"
              >
              <span v-else class="text-gray-400">Brak zdjęcia</span>
            </td>
            <td class="border-t px-6 py-3">{{ article.title || '(bez tytułu)' }}</td>
            <td class="border-t px-6 py-3">
              <neutral-pill
                v-if="article.site_context_label"
                :label="article.site_context_label"
              />
              <span v-else class="text-gray-400">—</span>
            </td>
            <td class="border-t px-6 py-3 font-mono text-xs text-gray-600">{{ article.slug }}</td>
            <td class="border-t px-6 py-3">
              <span :class="[
                'inline-flex rounded-full px-2 text-xs font-semibold leading-5',
                article.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-800'
              ]">
                {{ article.status === 'published' ? 'Opublikowana' : 'Szkic' }}
              </span>
            </td>
            <td class="border-t px-6 py-3 text-sm text-gray-500">
              {{ formatDate(article.published_at) }}
            </td>
            <td class="border-t px-6 py-3">
              <div class="flex items-center gap-2">
                <a
                  v-if="article.slug"
                  class="btn-action-view"
                  :href="frontofficeHref(article)"
                  target="_blank"
                  title="Zobacz na stronie"
                >
                  <icon name="eye" class="mr-1.5 h-3.5 w-3.5" />
                  Podgląd na stronie
                </a>
                <Link class="btn-action-edit" :href="routes.edit(article.id)" title="Edytuj">
                  Edytuj
                </Link>
                <button class="btn-action-delete" type="button" @click="destroy(article)" title="Usuń">
                  Usuń
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="7" class="border-t px-6 py-6 text-center text-gray-500">
              {{ emptyMessage }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Pagination :meta="articles.meta" class="mt-6" />
  </div>
</template>

<script>
import { Head, Link, router } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import Pagination from '@/Shared/Pagination.vue';
import pickBy from 'lodash/pickBy';
import throttle from 'lodash/throttle';
import { createLayoutHelper } from '@/utils/layoutHelper';
import { createArticleRouteHelper } from '@/utils/createArticleRouteHelper';
import NeutralPill from '@/Shared/NeutralPill.vue';

export default {
  components: { Head, Link, Icon, Pagination, NeutralPill },
  layout: createLayoutHelper,
  props: {
    articles: { type: Object, required: true },
    filters: { type: Object, default: () => ({}) },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_options: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: {
        title: this.filters.title || '',
        slug: this.filters.slug || '',
      },
    };
  },
  computed: {
    routes() {
      return createArticleRouteHelper(null, this.$routes);
    },
    hasActiveFilters() {
      return !!(this.form.title?.trim() || this.form.slug?.trim());
    },
    emptyMessage() {
      return this.hasActiveFilters
        ? 'Brak aktualności pasujących do filtrów.'
        : 'Brak aktualności. Kliknij "Nowa aktualność".';
    },
  },
  watch: {
    form: {
      handler: throttle(function () {
        const query = pickBy({
          title: this.form.title?.trim(),
          slug: this.form.slug?.trim(),
        });
        this.$inertia.get(
          this.routes.index(Object.keys(query).length ? query : {}),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            only: ['articles'],
          },
        );
      }, 150),
      deep: true,
    },
  },
  methods: {
    resetFilters() {
      this.form.title = '';
      this.form.slug = '';
    },
    formatDate(iso) {
      if (!iso) return '-';
      try {
        return new Date(iso).toLocaleString('pl-PL', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch {
        return iso;
      }
    },
    frontofficeHref(article) {
      return this.$routes.article(article.slug);
    },
    destroy(article) {
      if (!confirm(`Usunąć aktualność "${article.title}"?`)) return;
      router.delete(this.routes.destroy(article.id), { preserveScroll: true });
    },
  },
};
</script>
